import { getMonthShortAbbreviation, MonthYear, stringToMonthYear } from '@common/neo.tax';
import {
  colors,
  ControlledAutocompleteInput,
  InputFormContainer,
  Modal,
  Spinner,
  Stack,
  TextArea,
  Typography,
  useModal,
} from '@ui-components/neo.tax';
import { ReactComponent as CheckIcon } from '@ui-components/neo.tax/dist/icons/check.svg';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAscContext } from '@neo.tax/asc/context';
import {
  useAscProjectApprovalStatus,
  useClearAscProjectApproval,
  useSetAscProjectApproval,
  useUpdateAscProject,
  type AscProject,
} from '@neo.tax/asc/hooks';
import {
  evictCache,
  formatContributorNameWithEmail,
  noopFormSubmit,
  useCurrentUser,
  AscProjectApprovalStatus,
} from '@neo.tax/shared';
import { OrganizationMember } from '@neo.tax/shared/hooks/useCurrentUser';

import {
  StyledApprovalStatusContainer,
  StyledApprovalStatusText,
  StyledBlockingMessage,
  StyledButton,
  StyledPartialApprovalContainer,
  StyledPartialApprovalText,
} from './asc-project-approval-pane.styled';
import { useAscProjectApprovalAnalyticIds } from './hooks';
import { ResetApprovalConfirmationModal } from './ResetApprovalConfirmationModal';

const formatMonthYearString = (monthYearString: string | null | undefined): string => {
  if (!monthYearString) return '';
  try {
    const monthYear = stringToMonthYear(monthYearString);
    const monthFormatted = getMonthShortAbbreviation(monthYear.month);
    const yearFormatted = monthYear.year.toString().slice(-2);
    return `${monthFormatted} '${yearFormatted}`;
  } catch {
    return monthYearString;
  }
};

const formatMonthYear = (monthYear: MonthYear): string => {
  const monthFormatted = getMonthShortAbbreviation(monthYear.month);
  const yearFormatted = monthYear.year.toString().slice(-2);
  return `${monthFormatted} '${yearFormatted}`;
};

interface AscProjectApprovalPaneProps {
  ascProject: AscProject;
  members: OrganizationMember[];
}

interface ProjectOwnerForm {
  projectOwnerId: string | null;
}

const UPDATE_PROJECT_OWNER_REQUEST_KEY = 'UPDATE_PROJECT_OWNER_REQUEST_KEY';

export const AscProjectApprovalPane = ({ ascProject, members }: AscProjectApprovalPaneProps): ReactElement => {
  const { fiscalYearId, period, periodName } = useAscContext();
  const { approvalStatus, loading: approvalStatusLoading } = useAscProjectApprovalStatus(ascProject.id);
  const { user } = useCurrentUser();
  const [comment, setComment] = useState('');
  const { isOpen: isResetModalOpen, setIsOpen: setResetModalOpen } = useModal();
  const {
    approveBtnId,
    clearProjectOwnerBtnId,
    commentId,
    projectOwnerId: projectOwnerAnalyticId,
    resetBtnId,
  } = useAscProjectApprovalAnalyticIds();

  const { control, setValue, watch } = useForm<ProjectOwnerForm>({
    mode: 'onChange',
    defaultValues: {
      projectOwnerId: ascProject.projectOwnerId,
    },
  });

  const [updateAscProject] = useUpdateAscProject({
    ascProject,
    fiscalYearId,
    periodName,
    context: {
      debounceKey: UPDATE_PROJECT_OWNER_REQUEST_KEY,
      serializationKey: UPDATE_PROJECT_OWNER_REQUEST_KEY,
    },
  });

  const [setAscProjectApproval, { loading: setApprovalLoading }] = useSetAscProjectApproval();
  const [clearAscProjectApproval, { loading: clearLoading }] = useClearAscProjectApproval();

  const currentUserId = user?.id;
  const isCurrentUserOwner = currentUserId === ascProject.projectOwnerId;
  const hasProjectDates = ascProject.startDate !== null && ascProject.endDate !== null;
  const isApproved = approvalStatus?.status === AscProjectApprovalStatus.Approved;
  const hasOverallApproval = approvalStatus?.approvedThroughMonthYear != null;

  const watchedProjectOwnerId = watch('projectOwnerId');
  const canApprove = hasProjectDates && watchedProjectOwnerId && isCurrentUserOwner;

  const memberOptions = useMemo(
    () =>
      members.map((member) => ({
        ...member,
        displayName: formatContributorNameWithEmail(member.fullName, member.email),
      })),
    [members],
  );

  const handleClearProjectOwner = useCallback((): void => {
    setValue('projectOwnerId', null);
  }, [setValue]);

  const handleApprove = useCallback(async (): Promise<void> => {
    await setAscProjectApproval({
      ascProjectId: ascProject.id,
      fiscalYearId,
      periodName,
      comment: comment || null,
    });
    setComment('');
  }, [ascProject.id, comment, fiscalYearId, periodName, setAscProjectApproval]);

  const handleResetApproval = useCallback(async (): Promise<void> => {
    await clearAscProjectApproval({
      ascProjectId: ascProject.id,
      fiscalYearId,
      periodName,
    });
    setResetModalOpen(false);
  }, [ascProject.id, clearAscProjectApproval, fiscalYearId, periodName, setResetModalOpen]);

  // Sync form with project owner changes
  useEffect(() => {
    setValue('projectOwnerId', ascProject.projectOwnerId ?? null);
  }, [ascProject.projectOwnerId, setValue]);

  // Update project owner when form value changes
  useEffect(() => {
    const subscription = watch((values) => {
      if (values.projectOwnerId !== ascProject.projectOwnerId) {
        updateAscProject(
          { projectOwnerId: values.projectOwnerId ?? null },
          {
            update: (cache) => {
              evictCache(cache, ['getAscWages', 'getAscWageSummary']);
            },
          },
        );
      }
    });
    return (): void => subscription.unsubscribe();
  }, [ascProject.projectOwnerId, updateAscProject, watch]);

  // Find approver name from members list (for the period's end month approval)
  const approverName = members.find((m) => m.id === approvalStatus?.periodApprovalByUserId)?.fullName ?? 'Unknown';

  // Format approval date
  const formatApprovalDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Stack direction="column" paddingX={4} paddingY={4} fullHeight as="form" onSubmit={noopFormSubmit}>
      {/* Project Owner Dropdown */}
      <ControlledAutocompleteInput
        control={control}
        id={projectOwnerAnalyticId}
        label="Project Owner"
        name="projectOwnerId"
        options={memberOptions}
        optionValueKey="id"
        optionDisplayKey="displayName"
        searchKeys={['fullName', 'email']}
        placeholder="Select owner"
        allowCreate={false}
        action={
          ascProject.projectOwner?.id && (
            <Typography
              as="button"
              fontSize="sm"
              color={colors.primary[600]}
              id={clearProjectOwnerBtnId}
              onClick={handleClearProjectOwner}
            >
              Clear
            </Typography>
          )
        }
      />

      {approvalStatusLoading ? (
        <Stack direction="column" alignItems="center" justifyContent="center">
          <Spinner />
        </Stack>
      ) : (
        <Stack direction="column">
          {/* Blocking message if no project dates */}
          {!hasProjectDates && (
            <StyledBlockingMessage direction="column" gap={2}>
              <Typography fontSize="sm" color={colors.orange[800]} fontWeight="medium">
                Approval Not Available
              </Typography>
              <Typography fontSize="sm" color={colors.orange[700]}>
                Please set project start and end dates before approval.
              </Typography>
            </StyledBlockingMessage>
          )}

          {/* Approval Status Display - Fully Approved */}
          {hasProjectDates && isApproved && (
            <StyledApprovalStatusContainer direction="column" gap={3}>
              <Stack direction="row" alignItems="center" gap={2}>
                <CheckIcon width={20} height={20} color={colors.green[600]} />
                <StyledApprovalStatusText fontSize="sm" fontWeight="medium">
                  Approved
                </StyledApprovalStatusText>
              </Stack>
              {approvalStatus?.periodApprovalByUserId && (
                <Stack direction="column" gap={1}>
                  <Typography fontSize="sm" color={colors.gray[600]}>
                    Approved by: {approverName}
                  </Typography>
                  <Typography fontSize="sm" color={colors.gray[600]}>
                    Date: {formatApprovalDate(approvalStatus?.periodApprovalDatetime ?? null)}
                  </Typography>
                  {approvalStatus?.periodApprovalComment && (
                    <Typography fontSize="sm" color={colors.gray[600]}>
                      Comment: {approvalStatus.periodApprovalComment}
                    </Typography>
                  )}
                </Stack>
              )}
            </StyledApprovalStatusContainer>
          )}

          {/* Approval Status Display - Partially Approved (more subtle) */}
          {hasProjectDates && hasOverallApproval && !isApproved && (
            <StyledPartialApprovalContainer direction="row" alignItems="center" gap={2}>
              <CheckIcon width={14} height={14} color={colors.green[600]} />
              <StyledPartialApprovalText fontSize="xs">
                Approved until {formatMonthYearString(approvalStatus?.approvedThroughMonthYear)}
              </StyledPartialApprovalText>
            </StyledPartialApprovalContainer>
          )}

          {/* Comment Field - Show when not fully approved */}
          {hasProjectDates && !isApproved && (
            <InputFormContainer label="Comment (optional)" inputId={commentId}>
              <TextArea
                id={commentId}
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter your comments..."
                rows={3}
                disabled={!isCurrentUserOwner}
              />
            </InputFormContainer>
          )}

          {/* Action Buttons */}
          <Stack direction="column" gap={3}>
            {/* Approve/Extend Button - Show when not fully approved */}
            {hasProjectDates && !isApproved && (
              <>
                <StyledButton
                  id={approveBtnId}
                  size="base"
                  variant="primary"
                  rounded="regular"
                  onClick={handleApprove}
                  disabled={!canApprove || setApprovalLoading || !isCurrentUserOwner}
                >
                  {setApprovalLoading
                    ? 'Approving...'
                    : hasOverallApproval
                      ? `Extend Approval to ${formatMonthYear(period.endMonthYear)}`
                      : `Approve to ${formatMonthYear(period.endMonthYear)}`}
                </StyledButton>
                {!isCurrentUserOwner && (
                  <Typography fontSize="sm" color={colors.gray[500]}>
                    Only project owners can approve.
                  </Typography>
                )}
              </>
            )}

            {/* Reset Button - Show for owner when has any approval */}
            {hasProjectDates && isCurrentUserOwner && hasOverallApproval && (
              <StyledButton
                id={resetBtnId}
                size="base"
                variant="rsbry"
                rounded="regular"
                onClick={() => setResetModalOpen(true)}
                disabled={clearLoading}
              >
                Reset Approval
              </StyledButton>
            )}
          </Stack>
        </Stack>
      )}

      {/* Reset Confirmation Modal */}
      <Modal isOpen={isResetModalOpen} setIsOpen={setResetModalOpen}>
        <ResetApprovalConfirmationModal
          onClose={() => setResetModalOpen(false)}
          onConfirm={handleResetApproval}
          loading={clearLoading}
        />
      </Modal>
    </Stack>
  );
};
